import ConciliacaoService from '@/services/ConciliacaoService';

export const RODADAS_POR_PAGINA = 10;
export const RODADAS_BUFFER_INICIAL = 30;
export const RODADAS_PAGINAS_A_FRENTE = 2;

let contadorTemporario = 0;

function novoIdTemporario() {
  contadorTemporario += 1;
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `tmp-${crypto.randomUUID()}`;
  }
  return `tmp-${Date.now()}-${contadorTemporario}`;
}

function copiarMapa(valor) {
  if (!valor || typeof valor !== 'object' || Array.isArray(valor)) return {};
  return JSON.parse(JSON.stringify(valor));
}

function mesmaLista(a, b) {
  return JSON.stringify((a || []).map(String)) === JSON.stringify((b || []).map(String));
}

function estadoInicial() {
  return {
    buffer: [],
    criadasNoFim: [],
    excluidos: [],
    totalServidor: null,
    carregadasServidor: 0,
    nextCursor: null,
    hasMore: false,
    versao: null
  };
}

function cardDoServidor(row) {
  const campos = (row.campos || []).map(String);
  return {
    chave: row.id,
    id: row.id,
    idTemporario: null,
    sequencia: row.sequencia ?? null,
    peso: row.peso ?? null,
    origem: row.origem || null,
    tipoOrdenacao: row.tipoOrdenacao || null,
    campos,
    estadoPorCampo: copiarMapa(row.estadoPorCampo),
    itensAtivos: copiarMapa(row.itensAtivos),
    camposOriginais: [...campos],
    arrastado: false
  };
}

function cardNovo({ campos = [], estadoPorCampo = {}, tipoOrdenacao = null, itensAtivos = {} } = {}) {
  const idTemporario = novoIdTemporario();
  return {
    chave: idTemporario,
    id: null,
    idTemporario,
    sequencia: null,
    peso: null,
    origem: 'MANUAL',
    tipoOrdenacao: tipoOrdenacao || null,
    campos: (campos || []).map(String),
    estadoPorCampo: copiarMapa(estadoPorCampo),
    itensAtivos: copiarMapa(itensAtivos),
    camposOriginais: null,
    arrastado: false
  };
}

function camposEditados(card) {
  return !!card.id && !mesmaLista(card.campos, card.camposOriginais);
}

function restantes(estado) {
  if (!estado.hasMore) return 0;
  const total = estado.totalServidor != null ? Number(estado.totalServidor) : estado.carregadasServidor;
  return Math.max(0, total - estado.carregadasServidor);
}

function totalExibido(estado) {
  return estado.buffer.length + restantes(estado) + estado.criadasNoFim.length;
}

function lotePendente(estado) {
  return (
    estado.excluidos.length > 0 ||
    estado.criadasNoFim.length > 0 ||
    estado.buffer.some((c) => c.idTemporario || c.arrastado || camposEditados(c))
  );
}

function absorverCriadasNoFim(estado) {
  if (estado.hasMore || !estado.criadasNoFim.length) return estado;
  return { ...estado, buffer: [...estado.buffer, ...estado.criadasNoFim], criadasNoFim: [] };
}

function anexarPagina(estado, page = {}) {
  const rows = Array.isArray(page.data) ? page.data : [];
  const conhecidos = new Set([...estado.buffer.map((c) => c.id).filter(Boolean), ...estado.excluidos]);
  const novos = rows.filter((r) => r && r.id && !conhecidos.has(r.id)).map(cardDoServidor);
  const total = page.total != null ? page.total : page.totalRodadas;

  return absorverCriadasNoFim({
    ...estado,
    buffer: [...estado.buffer, ...novos],
    carregadasServidor: estado.carregadasServidor + rows.length,
    nextCursor: page.nextCursor || page.next_cursor || null,
    hasMore: !!(page.hasMore ?? page.has_more),
    totalServidor: total != null ? Number(total) : estado.totalServidor
  });
}

function localizar(estado, chave) {
  const i = estado.buffer.findIndex((c) => c.chave === chave);
  if (i >= 0) return { lista: 'buffer', indice: i };
  const j = estado.criadasNoFim.findIndex((c) => c.chave === chave);
  if (j >= 0) return { lista: 'criadasNoFim', indice: j };
  return null;
}

function moverRodada(estado, de, para) {
  const buffer = [...estado.buffer];
  if (de === para || de < 0 || de >= buffer.length || para < 0 || para >= buffer.length) return estado;
  const [card] = buffer.splice(de, 1);
  buffer.splice(para, 0, { ...card, arrastado: true });
  return { ...estado, buffer };
}

function editarCampos(estado, chave, { campos, estadoPorCampo }) {
  const pos = localizar(estado, chave);
  if (!pos) return estado;
  const lista = [...estado[pos.lista]];
  lista[pos.indice] = {
    ...lista[pos.indice],
    campos: (campos || []).map(String),
    estadoPorCampo: copiarMapa(estadoPorCampo)
  };
  return { ...estado, [pos.lista]: lista };
}

function excluirRodada(estado, chave) {
  const pos = localizar(estado, chave);
  if (!pos) return estado;
  const lista = [...estado[pos.lista]];
  const [card] = lista.splice(pos.indice, 1);
  const excluidos = card.id ? [...estado.excluidos, card.id] : estado.excluidos;
  return { ...estado, [pos.lista]: lista, excluidos };
}

function duplicarRodada(estado, chave) {
  const pos = localizar(estado, chave);
  if (!pos) return estado;
  const lista = [...estado[pos.lista]];
  const original = lista[pos.indice];
  lista.splice(
    pos.indice + 1,
    0,
    cardNovo({
      campos: original.campos,
      estadoPorCampo: original.estadoPorCampo,
      tipoOrdenacao: original.tipoOrdenacao,
      itensAtivos: original.itensAtivos
    })
  );
  return { ...estado, [pos.lista]: lista };
}

function adicionarRodada(estado, dados) {
  const card = cardNovo(dados);
  if (estado.hasMore) return { ...estado, criadasNoFim: [...estado.criadasNoFim, card] };
  return { ...estado, buffer: [...estado.buffer, card] };
}

function filtrarEstado(estadoPorCampo, campos) {
  const ids = new Set(campos.map(String));
  const filtrado = {};
  Object.keys(estadoPorCampo || {}).forEach((id) => {
    if (ids.has(id)) filtrado[id] = estadoPorCampo[id];
  });
  return filtrado;
}

function camposDoItem(card, prepararCampos) {
  const preparado = prepararCampos ? prepararCampos(card.campos) : { campos: card.campos, estadoPorCampo: {} };
  const campos = [...preparado.campos].map(String);
  return {
    campos,
    estadoPorCampo: { ...filtrarEstado(card.estadoPorCampo, campos), ...(preparado.estadoPorCampo || {}) }
  };
}

function itemCriado(card, posicao, prepararCampos) {
  const item = { idTemporario: card.idTemporario, ...camposDoItem(card, prepararCampos) };
  if (card.tipoOrdenacao) item.tipoOrdenacao = card.tipoOrdenacao;
  if (card.itensAtivos && Object.keys(card.itensAtivos).length) item.itensAtivos = copiarMapa(card.itensAtivos);
  item.posicao = posicao;
  return item;
}

function montarLote(estado, { prepararCampos = null } = {}) {
  const rodadas = estado.excluidos.map((id) => ({ id, excluir: true }));

  estado.buffer.forEach((card, i) => {
    const posicao = i + 1;
    if (card.idTemporario) {
      rodadas.push(itemCriado(card, posicao, prepararCampos));
      return;
    }
    const editado = camposEditados(card);
    if (!editado && !card.arrastado) return;
    const item = { id: card.id };
    if (editado) Object.assign(item, camposDoItem(card, prepararCampos));
    if (card.arrastado) item.posicao = posicao;
    rodadas.push(item);
  });

  const inicioFim = estado.buffer.length + restantes(estado);
  estado.criadasNoFim.forEach((card, j) => {
    rodadas.push(itemCriado(card, inicioFim + j + 1, prepararCampos));
  });

  return estado.versao != null ? { versao: estado.versao, rodadas } : { rodadas };
}

function cursorAposPosicao(n) {
  return String(n);
}

function aplicarRespostaLote(estado, resposta = {}) {
  const retorno = Array.isArray(resposta.rodadas) ? resposta.rodadas : [];
  const porTemporario = {};
  const porId = {};
  retorno.forEach((r) => {
    if (r.idTemporario) porTemporario[r.idTemporario] = r;
    if (r.id) porId[r.id] = r;
  });

  const restantesAntes = restantes(estado);
  let inconsistente = false;

  const buffer = estado.buffer.map((card, i) => {
    const r = card.idTemporario ? porTemporario[card.idTemporario] : porId[card.id];
    const id = card.idTemporario ? r?.id || null : card.id;
    if (!id) inconsistente = true;
    return {
      ...card,
      id,
      idTemporario: id ? null : card.idTemporario,
      sequencia: r?.sequencia ?? i + 1,
      peso: r?.peso ?? card.peso,
      origem: r?.origem || card.origem,
      camposOriginais: [...card.campos],
      arrastado: false
    };
  });

  const totalCalculado = buffer.length + restantesAntes + estado.criadasNoFim.length;
  const total = resposta.totalRodadas != null ? Number(resposta.totalRodadas) : totalCalculado;

  return {
    estado: {
      ...estado,
      buffer,
      criadasNoFim: [],
      excluidos: [],
      carregadasServidor: buffer.length,
      totalServidor: total,
      nextCursor: estado.hasMore ? cursorAposPosicao(buffer.length) : null,
      versao: resposta.versao != null ? Number(resposta.versao) : estado.versao
    },
    inconsistente
  };
}

export default {
  data: () => ({
    loadingRodadas: false,
    rodadasLote: estadoInicial(),
    rodadasPageIndex: 0,
    rodadasFila: Promise.resolve(),
    rodadasCiclo: 0,
    rodadasBloqueadas: false,
    rodadasCamposConfig: [],
    rodadasEstadoPorCampo: {},
    rodadasTipoOrdenacao: null
  }),
  computed: {
    rodadasPagina() {
      const inicio = this.rodadasPageIndex * RODADAS_POR_PAGINA;
      return this.rodadasLote.buffer.slice(inicio, inicio + RODADAS_POR_PAGINA);
    },
    rodadasTotalExibido() {
      return totalExibido(this.rodadasLote);
    },
    rodadasTemProxima() {
      return (this.rodadasPageIndex + 1) * RODADAS_POR_PAGINA < this.rodadasTotalExibido;
    }
  },
  methods: {
    enfileirarRodadas(fn) {
      const execucao = this.rodadasFila.then(() => fn());
      this.rodadasFila = execucao.catch(() => {});
      return execucao;
    },
    validarRodadasBuffer() {
      const { buffer, criadasNoFim } = this.rodadasLote;
      const i = buffer.findIndex((c) => !c.campos?.length);
      if (i >= 0) return `Selecione ao menos um campo na ${i + 1}ª rodada.`;
      const j = criadasNoFim.findIndex((c) => !c.campos?.length);
      if (j >= 0) return `Selecione ao menos um campo na ${buffer.length + restantes(this.rodadasLote) + j + 1}ª rodada.`;
      return true;
    },

    async salvarLoteRodadas({ silencioso = false, marcarConcluida = false } = {}) {
      if (!this.entityId) return false;
      const pendente = lotePendente(this.rodadasLote);
      if (!pendente && !(marcarConcluida && !this.entity?.rodadas_ok)) return false;

      const valid = this.validarRodadasBuffer();
      if (valid !== true) {
        this.$toast.error(valid, '', { position: 'topRight' });
        throw new Error(valid);
      }

      const tabs = this.$refs.wizardTabs;
      const ciclo = this.rodadasCiclo;
      this.loadingRodadas = true;
      try {
        const { data } = await this.enfileirarRodadas(() => {
          const lote = montarLote(this.rodadasLote, { prepararCampos: (campos) => tabs.prepararCamposRodada(campos) });
          return ConciliacaoService.salvarRodadas(this.entityId, lote);
        });
        const resposta = data?.data || {};
        if (resposta.entity) this.applyEntity(resposta.entity);
        if (ciclo === this.rodadasCiclo) {
          const { estado, inconsistente } = aplicarRespostaLote(this.rodadasLote, resposta);
          this.rodadasLote = estado;
          if (inconsistente) await this.carregarRodadas('reset-get');
        }
        if (!silencioso) {
          this.$toast.success(data?.message || 'Rodadas salvas.', '', { position: 'topRight', timeout: 2000 });
        }
        return true;
      } catch (err) {
        await this.tratarErroLoteRodadas(err);
        throw err;
      } finally {
        this.loadingRodadas = false;
      }
    },
    async tratarErroLoteRodadas(err) {
      const status = err?.status || err?.response?.status;
      const details = (err?.data || err?.response?.data)?.error?.details || {};
      const opts = { position: 'topRight' };

      if (status === 409 && details.erro === 'CONFLITO_VERSAO') {
        this.$toast.warning(
          'A lista de rodadas foi alterada em outro lugar enquanto você editava. Suas alterações não foram salvas e a lista foi recarregada.',
          '',
          { ...opts, timeout: 8000 }
        );
        await this.carregarRodadas('reset-get');
        return;
      }
      if (status === 409 && details.erro === 'GERACAO_EM_ANDAMENTO') {
        this.$toast.warning('As rodadas estão sendo geradas. A lista será recarregada.', '', opts);
        await this.carregarRodadas('reset-get');
        return;
      }
      if (status === 423) {
        this.rodadasBloqueadas = true;
        this.$toast.error('Há uma execução em andamento. A edição das rodadas fica bloqueada até ela terminar.', '', opts);
        return;
      }
      this.$toast.error(this.apiError(err, 'Erro ao salvar rodadas.'), '', opts);
    },
    async mudarPaginaRodadas(mode) {
      if (this.loadingRodadas) return;
      if (mode === 'prev') {
        if (this.rodadasPageIndex > 0) this.rodadasPageIndex -= 1;
        return;
      }
      if (!this.rodadasTemProxima) return;

      try {
        await this.salvarLoteRodadas({ silencioso: true });
      } catch {
        return;
      }

      const destino = this.rodadasPageIndex + 1;
      if (this.rodadasLote.hasMore && this.rodadasLote.buffer.length < (destino + 1) * RODADAS_POR_PAGINA) {
        this.loadingRodadas = true;
        try {
          await this.garantirBuffer(destino);
        } finally {
          this.loadingRodadas = false;
        }
      }
      if (destino * RODADAS_POR_PAGINA >= this.rodadasLote.buffer.length) return;
      this.rodadasPageIndex = destino;
      this.garantirBuffer();
    },
    async garantirBuffer(pageIndex = this.rodadasPageIndex) {
      if (!this.entityId) return;
      const alvo = (pageIndex + 1 + RODADAS_PAGINAS_A_FRENTE) * RODADAS_POR_PAGINA;
      const ciclo = this.rodadasCiclo;
      try {
        await this.enfileirarRodadas(async () => {
          const lote = this.rodadasLote;
          const falta = alvo - lote.buffer.length;
          if (ciclo !== this.rodadasCiclo || falta <= 0 || !lote.hasMore || !lote.nextCursor) return;
          const { data } = await ConciliacaoService.listarRodadas(this.entityId, {
            cursor: lote.nextCursor,
            limit: falta,
            incluirTotal: true
          });
          if (ciclo !== this.rodadasCiclo) return;
          if (!Array.isArray(data?.data?.data)) throw new Error('Resposta de rodadas sem a lista.');
          this.rodadasLote = anexarPagina(this.rodadasLote, data.data);
        });
      } catch (err) {
        console.warn('Falha ao buscar mais rodadas.', err);
      }
    },
    aplicarLeituraInicial(page) {
      if (page.entity) {
        this.applyEntity(page.entity);
      }
      const versao = page.versao ?? page.entity?.versao ?? null;
      this.rodadasLote = { ...anexarPagina(estadoInicial(), page), versao: versao != null ? Number(versao) : null };
      this.rodadasPageIndex = 0;
      const primeira = this.rodadasLote.buffer[0];
      if (!this.rodadasTipoOrdenacao && primeira?.tipoOrdenacao) {
        this.rodadasTipoOrdenacao = primeira.tipoOrdenacao;
      }
      if (page.campos?.length) {
        this.rodadasCamposConfig = page.campos.map(String);
      }
      if (page.estadoPorCampo) {
        this.rodadasEstadoPorCampo = page.estadoPorCampo;
      }
      this.garantirBuffer();
    },
    payloadGeracaoRodadasPadrao() {
      const partidaIds = this.idsCamposPartidaAtuais();
      const fromAba = this.$refs.wizardTabs?.getRodadasGeracaoPayload?.();
      if (fromAba?.campos?.length && this.camposExistemNaPartida(fromAba.campos, partidaIds)) {
        return fromAba;
      }

      if (this.rodadasCamposConfig?.length && this.camposExistemNaPartida(this.rodadasCamposConfig, partidaIds)) {
        return {
          campos: this.rodadasCamposConfig.map(String),
          estadoPorCampo: this.rodadasEstadoPorCampo || {},
          tipoOrdenacao: this.rodadasTipoOrdenacao || 'PADRAO',
          limit: RODADAS_BUFFER_INICIAL
        };
      }

      const camposPartida = this.entity?.parametros?.bases?.PARTIDA?.campos || [];
      const ordenados = [...camposPartida].filter((c) => c && c.id).sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));

      let campos = ordenados.slice(0, 6).map((c) => String(c.id));
      const tuc = ordenados.find((c) => {
        const nome = String(c.nomeExibicao || c.colunaOrigem || '').toUpperCase();
        return nome === 'TUC';
      });
      const estadoPorCampo = {};
      if (tuc) {
        const tucId = String(tuc.id);
        if (!campos.includes(tucId)) {
          if (campos.length >= 6) campos[campos.length - 1] = tucId;
          else campos.push(tucId);
        }
        estadoPorCampo[tucId] = 'BLINDADO';
      }

      return {
        campos,
        estadoPorCampo,
        limit: RODADAS_BUFFER_INICIAL
      };
    },
    idsCamposPartidaAtuais() {
      return new Set((this.entity?.parametros?.bases?.PARTIDA?.campos || []).filter((c) => c && c.id).map((c) => String(c.id)));
    },
    camposExistemNaPartida(campos, partidaIds = null) {
      const ids = partidaIds || this.idsCamposPartidaAtuais();
      if (!ids.size || !campos?.length) return false;
      return campos.every((id) => ids.has(String(id)));
    },
    rodadasConfigAindaValida() {
      if (!this.entity?.rodadas_ok) return false;
      const camposConfigSalvos = this.entity?.parametros?.rodadasConfig?.campos;
      const candidatos = camposConfigSalvos?.length ? camposConfigSalvos : this.rodadasCamposConfig;
      if (!candidatos?.length) return true;
      return this.camposExistemNaPartida(candidatos);
    },
    gerarRodadasComPrioridade(payload) {
      if (!this.entityId) return;

      const body = payload?.campos?.length ? payload : this.payloadGeracaoRodadasPadrao();

      if (!body?.campos?.length) {
        this.$toast.error('Não há campos mapeados na Partida para gerar rodadas.', '', {
          position: 'topRight'
        });
        return;
      }

      // A geração refaz a lista: lote pendente e leituras em andamento deixam de valer.
      this.rodadasCiclo += 1;
      const ciclo = this.rodadasCiclo;
      this.loadingRodadas = true;
      this.rodadasLote = estadoInicial();
      this.rodadasPageIndex = 0;

      this.enfileirarRodadas(() => ConciliacaoService.gerarRodadas(this.entityId, body))
        .then(({ data }) => {
          if (ciclo !== this.rodadasCiclo) return;
          const page = data.data || {};
          if (page.async) {
            this.$toast.warning(data.message || 'Geração assíncrona enfileirada. Use a paginação em instantes.', '', {
              position: 'topRight'
            });
            return this.carregarRodadas('reset-get');
          }
          this.aplicarLeituraInicial(page);
        })
        .catch((err) => this.$toast.error(this.apiError(err, 'Erro ao gerar rodadas.'), '', { position: 'topRight' }))
        .finally(() => {
          this.loadingRodadas = false;
        });
    },
    // O AbaRodadas já espera o usuário parar e só emite quando a configuração mudou de fato.
    onPrioridadeChange(payload) {
      if (lotePendente(this.rodadasLote)) {
        this.$toast.warning('Mudar a configuração refaz a lista de rodadas.', '', {
          position: 'topRight',
          timeout: 6000
        });
      }
      this.gerarRodadasComPrioridade(payload);
    },
    carregarRodadas(mode = 'reset') {
      if (!this.entityId) return Promise.resolve();

      if (mode === 'reset') {
        return this.$nextTick(() => {
          if (this.rodadasConfigAindaValida()) {
            return this.carregarRodadas('reset-get');
          }
          this.rodadasCamposConfig = [];
          this.rodadasEstadoPorCampo = {};
          return this.gerarRodadasComPrioridade();
        });
      }

      this.rodadasCiclo += 1;
      const ciclo = this.rodadasCiclo;
      this.rodadasLote = estadoInicial();
      this.rodadasPageIndex = 0;
      this.rodadasBloqueadas = false;
      this.loadingRodadas = true;

      return this.enfileirarRodadas(() =>
        ConciliacaoService.listarRodadas(this.entityId, { limit: RODADAS_BUFFER_INICIAL, incluirTotal: true })
      )
        .then(({ data }) => {
          if (ciclo === this.rodadasCiclo) this.aplicarLeituraInicial(data.data || {});
        })
        .catch((err) => this.$toast.error(this.apiError(err, 'Erro ao listar rodadas.'), '', { position: 'topRight' }))
        .finally(() => {
          this.loadingRodadas = false;
        });
    },
    // Ações na lista: só no front, até o próximo envio do lote. Índices da página → posição no buffer.
    podeEditarRodadas() {
      return !this.loadingRodadas && !this.rodadasBloqueadas;
    },
    onRodadaMovida({ de, para }) {
      if (!this.podeEditarRodadas()) return;
      const base = this.rodadasPageIndex * RODADAS_POR_PAGINA;
      this.rodadasLote = moverRodada(this.rodadasLote, base + de, base + para);
    },
    onRodadaEditada({ chave, campos, estadoPorCampo }) {
      if (!this.podeEditarRodadas()) return;
      this.rodadasLote = editarCampos(this.rodadasLote, chave, { campos, estadoPorCampo });
    },
    onRodadaRemovida({ chave }) {
      if (!this.podeEditarRodadas() || this.rodadasTotalExibido <= 1) return;
      this.rodadasLote = excluirRodada(this.rodadasLote, chave);
      if (
        this.rodadasPageIndex > 0 &&
        !this.rodadasLote.hasMore &&
        this.rodadasPageIndex * RODADAS_POR_PAGINA >= this.rodadasLote.buffer.length
      ) {
        this.rodadasPageIndex -= 1;
      }
      this.garantirBuffer();
    },
    onRodadaDuplicada({ chave }) {
      if (!this.podeEditarRodadas()) return;
      this.rodadasLote = duplicarRodada(this.rodadasLote, chave);
    },
    onRodadaAdicionada(dados) {
      if (!this.podeEditarRodadas()) return;
      this.rodadasLote = adicionarRodada(this.rodadasLote, dados);
      this.$toast.info('Rodada adicionada ao final da lista.', '', { position: 'topRight', timeout: 2000 });
    }
  }
};
